/**
 * US state abbreviation → IANA timezone, for the states BonsaiBar operates in.
 *
 * A leaf module of pure data: it imports nothing, so it has no edges to get wrong.
 *
 * REVERSES A RECORDED DECISION, so the reasoning matters. This table used to live in
 * `jsonld-education-event.ts`, whose `eslint-disable max-lines` argued that splitting it
 * out "would break the pure-function colocation that makes unit testing easy". That
 * argument was weak even then — the drift-guard test imports the table just as easily
 * from here, and the re-export left every caller untouched — and it has since been
 * overtaken by a constraint that did not exist when it was written.
 *
 * The constraint: `venue-local-time.ts` now has to resolve a zone from a state
 * (`venueLocalHourToInstant`, BON-146), and `jsonld-education-event.ts` already imports
 * `venue-local-time.js`. Reading the table from its old home would close an import
 * cycle — and because it is a `const` export rather than a hoisted function, that cycle
 * fails at module init with a temporal-dead-zone error, not merely a lint warning.
 * Colocation is a preference; a module that cannot load is not.
 */

/**
 * US state abbreviation → IANA timezone.
 *
 * Covers every state the venue form offers (`US_STATES`), not just the ones we
 * operate in today: `createVenue` never writes `timeZone`, so an unmapped state
 * silently degrades a venue to UTC and every workshop there renders hours off.
 * A state that is selectable must therefore be resolvable.
 *
 * States spanning two zones (ID, ND, SD, TX, IN, …) map to the zone holding the
 * population, which is the one a venue is overwhelmingly likely to sit in. A
 * venue on the wrong side of such a line needs its `timeZone` set explicitly.
 *
 * Exported for the drift-guard test that asserts every offered state has a
 * mapping here.
 */
export const STATE_TO_IANA_TZ: Record<string, string> = {
  // Eastern Time
  CT: 'America/New_York',
  DC: 'America/New_York',
  FL: 'America/New_York',
  GA: 'America/New_York',
  IN: 'America/New_York',
  KY: 'America/New_York',
  MA: 'America/New_York',
  MD: 'America/New_York',
  ME: 'America/New_York',
  MI: 'America/New_York',
  NC: 'America/New_York',
  NH: 'America/New_York',
  NJ: 'America/New_York',
  NY: 'America/New_York',
  OH: 'America/New_York',
  PA: 'America/New_York',
  RI: 'America/New_York',
  DE: 'America/New_York',
  SC: 'America/New_York',
  VA: 'America/New_York',
  VT: 'America/New_York',
  WV: 'America/New_York',
  // Central Time
  AL: 'America/Chicago',
  AR: 'America/Chicago',
  IA: 'America/Chicago',
  IL: 'America/Chicago',
  KS: 'America/Chicago',
  LA: 'America/Chicago',
  MN: 'America/Chicago',
  MO: 'America/Chicago',
  MS: 'America/Chicago',
  NE: 'America/Chicago',
  OK: 'America/Chicago',
  TN: 'America/Chicago',
  ND: 'America/Chicago',
  SD: 'America/Chicago',
  TX: 'America/Chicago',
  WI: 'America/Chicago',
  // Mountain Time
  CO: 'America/Denver',
  ID: 'America/Boise',
  MT: 'America/Denver',
  NM: 'America/Denver',
  UT: 'America/Denver',
  WY: 'America/Denver',
  // Mountain Time (no DST)
  AZ: 'America/Phoenix',
  // Pacific Time
  CA: 'America/Los_Angeles',
  NV: 'America/Los_Angeles',
  OR: 'America/Los_Angeles',
  WA: 'America/Los_Angeles',
  // Alaska / Hawaii
  AK: 'America/Anchorage',
  HI: 'Pacific/Honolulu',
};
